import { z } from 'zod';

/// Prisma enum'larının ayna hâli. Frontend Prisma client'ı import etmemeli
/// (sunucu bağımlılığı), bu yüzden değerler burada da tanımlı.
export const AdPlatform = {
  META: 'META',
  GOOGLE: 'GOOGLE',
  TIKTOK: 'TIKTOK',
  /// Adapter YOK. `ConnectorRegistry.isImplemented()` false döner ve arayüz
  /// kartı "yakında" gösterir — enum değeri şimdi eklendi çünkü genişletme
  /// migration istiyor.
  OPENAI: 'OPENAI',
  /// Adapter YOK (Genişleme Adım 5 ve 6). `OPENAI` ile aynı gerekçeyle önden
  /// eklendi — enum genişletmesi migration istiyor ve tablo büyüdükçe
  /// pahalılaşıyor. `OPENAI`den tek farkı: `ConnectorRegistry.all()` bu ikisini
  /// listelemediği için arayüzde kart hiç ÇIKMIYOR, "yakında" bile demiyor.
  LINKEDIN: 'LINKEDIN',
  TWITTER: 'TWITTER',
} as const;
export type AdPlatform = (typeof AdPlatform)[keyof typeof AdPlatform];

/// Mağaza/pazaryeri platformları (Genişleme Adım 1–2). Prisma'daki
/// `StorePlatform` enum'ının aynası.
///
/// Adım 1'de `StoreConnection` bu enum'ı kullanmaya başladı. Değerlerin
/// Adım 0'da önden eklenmiş olması Adım 2'yi (pazaryerleri) şemaya hiç
/// dokunmadan koşabilir kılıyor — kimlik bilgisi şekli de bunun için
/// genelleştirildi (`storeCredentialsSchema`).
///
/// Adım 11'de yedi değer daha eklendi (eski APEX projesinde adapter'ları
/// yazılmış, ama oradaki 11 mağaza entegrasyonundan yalnız Shopify ve
/// Trendyol gerçekten bağlanmıştı — kalanı kanıtsız). Bugün bunlardan
/// yalnız `HEPSIBURADA`nın adapter'ı var; diğer altısı
/// `NotImplementedStoreConnector`da ve `isImplemented()` onlara `false`
/// diyor. Enum'da OLMALARI bir vaat değil, yalnız şemanın hazır olması.
export const StorePlatform = {
  SHOPIFY: 'SHOPIFY',
  TRENDYOL: 'TRENDYOL',
  IKAS: 'IKAS',
  TICIMAX: 'TICIMAX',
  HEPSIBURADA: 'HEPSIBURADA',
  N11: 'N11',
  AMAZON: 'AMAZON',
  ETSY: 'ETSY',
  WOOCOMMERCE: 'WOOCOMMERCE',
  TSOFT: 'TSOFT',
  IDEASOFT: 'IDEASOFT',
} as const;
export type StorePlatform = (typeof StorePlatform)[keyof typeof StorePlatform];

/**
 * Ürünün mağazadaki yayın durumu (Genişleme Adım 1). Prisma `ProductStatus`
 * aynası.
 *
 * `entityStatusSchema` (reklam hiyerarşisi) ile KARIŞTIRILMAMALI: orada
 * `ACTIVE/PAUSED` para harcamayı anlatıyor, burada bir ürünün vitrinde olup
 * olmadığını. `PAUSED` bir ürün diye bir şey yok.
 *
 * `ARCHIVED` yalnız Shopify'da birinci sınıf; anahtar kelime modeli farklı
 * olan pazaryerlerinde (Adım 2) satışa kapalı ürün `DRAFT`a düşer. Eşleme
 * adapter'ın İÇİNDE kalır.
 */
export const ProductStatus = {
  ACTIVE: 'ACTIVE',
  DRAFT: 'DRAFT',
  ARCHIVED: 'ARCHIVED',
} as const;
export type ProductStatus = (typeof ProductStatus)[keyof typeof ProductStatus];
export const productStatusSchema = z.enum(['ACTIVE', 'DRAFT', 'ARCHIVED']);

/**
 * Siparişin ÖDEME durumu. Prisma `OrderFinancialStatus` aynası.
 *
 * 🔴 `UNKNOWN` bir dolgu değil, bir CEVAP. Pazaryerlerinin bir kısmı ödeme
 * durumunu ayrı bir kavram olarak vermiyor (sipariş zaten ödenmiş kabul
 * ediliyor). `PAID` yazmak o platformlarda ciro raporunu şişirirdi; boş
 * bırakmak (`null`) ise "veri gelmedi" ile "böyle bir kavram yok"u aynı yere
 * koyardı. Ciro sorguları `PAID` + `PARTIALLY_PAID` sayar, `UNKNOWN` satırları
 * ayrı raporlanır.
 */
export const OrderFinancialStatus = {
  PENDING: 'PENDING',
  AUTHORIZED: 'AUTHORIZED',
  PAID: 'PAID',
  PARTIALLY_PAID: 'PARTIALLY_PAID',
  PARTIALLY_REFUNDED: 'PARTIALLY_REFUNDED',
  REFUNDED: 'REFUNDED',
  VOIDED: 'VOIDED',
  UNKNOWN: 'UNKNOWN',
} as const;
export type OrderFinancialStatus = (typeof OrderFinancialStatus)[keyof typeof OrderFinancialStatus];
export const orderFinancialStatusSchema = z.enum([
  'PENDING',
  'AUTHORIZED',
  'PAID',
  'PARTIALLY_PAID',
  'PARTIALLY_REFUNDED',
  'REFUNDED',
  'VOIDED',
  'UNKNOWN',
]);

/// Siparişin KARGO/hazırlık durumu. Ödeme durumundan ayrı: ödenmiş ama
/// gönderilmemiş sipariş normal bir hâl, ikisini tek kolona sıkıştırmak
/// "ödendi mi" ile "çıktı mı" sorularından birini cevapsız bırakırdı.
export const OrderFulfillmentStatus = {
  UNFULFILLED: 'UNFULFILLED',
  PARTIAL: 'PARTIAL',
  FULFILLED: 'FULFILLED',
  RESTOCKED: 'RESTOCKED',
  UNKNOWN: 'UNKNOWN',
} as const;
export type OrderFulfillmentStatus =
  (typeof OrderFulfillmentStatus)[keyof typeof OrderFulfillmentStatus];
export const orderFulfillmentStatusSchema = z.enum([
  'UNFULFILLED',
  'PARTIAL',
  'FULFILLED',
  'RESTOCKED',
  'UNKNOWN',
]);

export const WorkspaceRole = {
  OWNER: 'OWNER',
  ADMIN: 'ADMIN',
  MEMBER: 'MEMBER',
  VIEWER: 'VIEWER',
} as const;
export type WorkspaceRole = (typeof WorkspaceRole)[keyof typeof WorkspaceRole];

/// Yetki hiyerarşisi: sayı büyüdükçe yetki artar. Guard'lar bunu karşılaştırır.
export const ROLE_RANK: Record<WorkspaceRole, number> = {
  VIEWER: 0,
  MEMBER: 1,
  ADMIN: 2,
  OWNER: 3,
};

export const MetricLevel = {
  ACCOUNT: 'ACCOUNT',
  CAMPAIGN: 'CAMPAIGN',
  ADSET: 'ADSET',
  AD: 'AD',
  /// Yalnız arama ağı platformlarında (Google). Meta hesaplarında bu seviyede
  /// satır DOĞMAZ.
  KEYWORD: 'KEYWORD',
  /// 🔴 METRİK SEVİYESİ DEĞİL, AKSİYON HEDEFİ (Genişleme Adım 3).
  ///
  /// `metrics_daily`de bu seviyede satır YOK ve olmayacak. Değer yalnız
  /// `WorkflowActionLog.targetLevel` için var: AI katalog düzeltmesi bir
  /// ÜRÜNÜ hedefliyor ve onay kaydı hedefi yazabilmek zorunda. Ayrı bir onay
  /// yolu açmak yasaklıydı, o yüzden hedef tipi genişledi.
  ///
  /// `METRIC_DEFINITIONS[*].levels` bu değeri İÇERMEZ — hiçbir metrik ürün
  /// seviyesinde sorgulanamaz; ürün metriği `product_metrics_daily`de ve
  /// kendi sorgu yolu var.
  PRODUCT: 'PRODUCT',
  /// 🔴 METRİK SEVİYESİ DEĞİL, AKSİYON HEDEFİ (Şema borç turu).
  ///
  /// `PRODUCT`ın birebir aynı gerekçesi: kreatif onayı (`creative_approve`)
  /// bir KREATİFİ hedefliyor ve `WorkflowActionLog.targetLevel` o hedefi
  /// yazabilmek zorunda. `METRIC_DEFINITIONS[*].levels` bu değeri de
  /// İÇERMEZ — kreatif performansı `Ad.creativeId` köprüsünden, yani `AD`
  /// seviyesinden okunuyor.
  CREATIVE: 'CREATIVE',
} as const;
export type MetricLevel = (typeof MetricLevel)[keyof typeof MetricLevel];

/**
 * Ürün↔reklam köprüsünün hangi basamaktan çözüldüğü (Genişleme Adım 3).
 * Prisma `ProductMatchStrategy` enum'ının aynası — gerekçeler şemada.
 *
 * 🔴 Bu listede İSİM/BAŞLIK eşleştirmesi YOK ve eklenmeyecek.
 */
export const ProductMatchStrategy = {
  /// `shopify_<ÜLKE>_<ürünId>_<varyantId>` bileşik feed kimliği.
  FEED_COMPOSITE: 'FEED_COMPOSITE',
  /// Feed kimliği doğrudan platformun ürün/varyant kimliği.
  PLATFORM_REF: 'PLATFORM_REF',
  /// Tam SKU — yalnız workspace içinde TEKİLSE.
  SKU: 'SKU',
  /// Hiçbiri tutmadı. Satır yine yazılır, harcama "eşleşmeyen" olarak raporlanır.
  UNMATCHED: 'UNMATCHED',
} as const;
export type ProductMatchStrategy = (typeof ProductMatchStrategy)[keyof typeof ProductMatchStrategy];

/// Tasarım §8 — kredi maliyet tablosu. Tek kaynak burası; hem UI fiyat
/// gösterirken hem backend düşerken aynı sayıyı kullanır.
export const CREDIT_COST = {
  /**
   * Prompt üretme / iyileştirme — 17 Ağu 2026.
   *
   * Bu kalem `AD_COPY: 1`in DEVAMI, yeni bir kalem değil: reklam metni üretimi
   * üründen kaldırıldı ve LLM bütçesi prompt yazmaya devredildi. Sayı bilerek
   * aynı bırakıldı (1 kredi ≈ $0,15) çünkü ham maliyet de aynı büyüklükte —
   * görselli tek bir Claude çağrısı ~$0,01–0,03.
   *
   * 🔴 `CREDIT_COST_BY_CREATIVE_TYPE`e GİRMİYOR: prompt yardımı bir kreatif
   * SATIRI ÜRETMİYOR, yalnız metin döndürüyor. O tabloya koymak, kütüphanede
   * karşılığı olmayan bir tip uydurmak olurdu.
   */
  PROMPT_ASSIST: 1,
  IMPROVE: 2,
  RESIZE: 2,
  AD_IMAGE: 4,
  PRODUCT_VIDEO: 25,
  UGC_VIDEO: 40,
} as const;

// Dil listesi `locales.ts`te (29 Eyl 2026: 2 → 13 dil) — `localeSchema`,
// `Locale` ve dil meta verisi oradan export ediliyor.

// ───────────────────── Zod aynaları (sorgu/gövde doğrulaması) ─────────────────────
//
// Yukarıdaki `as const` nesneleri TİP tarafı için yeterli, ama gelen istek
// gövdesini doğrulayan bir şey değiller. Aşağıdakiler aynı değer kümesinin
// çalışma zamanı hâli — admin panelinde plan/durum seçen her form ve her
// controller bunları çalıştırıyor.
//
// Neden ikisi de var: `z.enum(...)` bir değer, `as const` nesnesi ise
// `WorkspaceRole.OWNER` gibi okunabilir sabitler veriyor. Birini diğerinden
// türetmek mümkün ama okunurluğu düşürüyor; küme küçük ve nadiren değişiyor.

export const workspaceRoleSchema = z.enum(['OWNER', 'ADMIN', 'MEMBER', 'VIEWER']);

/// Prisma `PlanTier`. Tip karşılığı `plans.ts`'te — rakamların yanında durması
/// için orada, doğrulama şeması diğer enum aynalarının yanında burada.
export const planTierSchema = z.enum(['TRIAL', 'STARTER', 'GROWTH', 'PRO', 'ENTERPRISE']);

/// Prisma `SubscriptionStatus`.
export const SubscriptionStatus = {
  TRIALING: 'TRIALING',
  ACTIVE: 'ACTIVE',
  PAST_DUE: 'PAST_DUE',
  /// Yumuşak sınır aşıldı — 7 gün ek süre (§7).
  GRACE: 'GRACE',
  CANCELED: 'CANCELED',
  EXPIRED: 'EXPIRED',
} as const;
export type SubscriptionStatus = (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];
export const subscriptionStatusSchema = z.enum([
  'TRIALING',
  'ACTIVE',
  'PAST_DUE',
  'GRACE',
  'CANCELED',
  'EXPIRED',
]);

/// Prisma `AutomationMode` — tasarım §4.2 guardrail'i.
export const AutomationMode = { SUGGEST: 'SUGGEST', AUTOPILOT: 'AUTOPILOT' } as const;
export type AutomationMode = (typeof AutomationMode)[keyof typeof AutomationMode];
export const automationModeSchema = z.enum(['SUGGEST', 'AUTOPILOT']);

/// Prisma `CreditEntryType` — değişmez defterin satır tipi (§8).
export const CreditEntryType = {
  MONTHLY_GRANT: 'MONTHLY_GRANT',
  TOPUP_PURCHASE: 'TOPUP_PURCHASE',
  RESERVE: 'RESERVE',
  COMMIT: 'COMMIT',
  REFUND: 'REFUND',
  EXPIRE: 'EXPIRE',
  /// Elle düzeltme — admin panelinden verilen/geri alınan kredi bu tiple yazılır.
  ADJUSTMENT: 'ADJUSTMENT',
} as const;
export type CreditEntryType = (typeof CreditEntryType)[keyof typeof CreditEntryType];
export const creditEntryTypeSchema = z.enum([
  'MONTHLY_GRANT',
  'TOPUP_PURCHASE',
  'RESERVE',
  'COMMIT',
  'REFUND',
  'EXPIRE',
  'ADJUSTMENT',
]);

// ═══════════════════════════════════════════════════════════════════════════
// Faz 1 aynaları — bağlantı, varlık, otomasyon, ajan, kreatif enum'ları
// ═══════════════════════════════════════════════════════════════════════════
//
// Hepsi Prisma'da da tanımlı. Sunucu tarafında `@mentorivo/db`'den de
// gelebilirler ama frontend Prisma client'ı import ETMEMELİ (Node bağımlılığı,
// üstelik `@ts-nocheck` taşıyan üretilmiş kod). Ayna deseni bu yüzden sürüyor.

/// Prisma `ConnectionStatus` — tasarım §9. Eşikler `integrations.ts`'te.
export const ConnectionStatus = {
  HEALTHY: 'HEALTHY',
  DEGRADED: 'DEGRADED',
  DISCONNECTED: 'DISCONNECTED',
} as const;
export type ConnectionStatus = (typeof ConnectionStatus)[keyof typeof ConnectionStatus];
export const connectionStatusSchema = z.enum(['HEALTHY', 'DEGRADED', 'DISCONNECTED']);

/// Prisma `EntityStatus` — kampanya/ad set/reklam durumu.
/**
 * Platformun BİLDİRDİĞİ yayın durumu (Meta'da `effective_status`).
 *
 * ⚠️ Son dört değer sonradan eklendi. Öncesinde `toEntityStatus()` Meta'nın
 * `DISAPPROVED` / `WITH_ISSUES` / `PENDING_REVIEW` / `IN_PROCESS` durumlarının
 * HEPSİNİ `PENDING`'e düşürüyordu: reklamı REDDEDİLMİŞ kullanıcı panelde
 * "beklemede" görüyor, neden harcama olmadığını anlayamıyordu.
 */
export const EntityStatus = {
  ACTIVE: 'ACTIVE',
  PAUSED: 'PAUSED',
  DELETED: 'DELETED',
  ARCHIVED: 'ARCHIVED',
  /// Yayına hazırlanıyor: inceleme sırası, faturalama bilgisi bekleniyor.
  PENDING: 'PENDING',
  /// Platform reddetti — beklemekle düzelmez, kullanıcı müdahale etmeli.
  DISAPPROVED: 'DISAPPROVED',
  /// Yayında ama dağıtım kısıtlı (Meta `WITH_ISSUES`).
  LIMITED: 'LIMITED',
  IN_REVIEW: 'IN_REVIEW',
  /// Öğrenme evresi. 🔴 Bu evredeki bir varlığın CPA'sına bakıp durdurmak,
  /// platformun optimizasyonunu yarıda kesmek demek.
  LEARNING: 'LEARNING',
} as const;
export type EntityStatus = (typeof EntityStatus)[keyof typeof EntityStatus];
export const entityStatusSchema = z.enum([
  'ACTIVE',
  'PAUSED',
  'DELETED',
  'ARCHIVED',
  'PENDING',
  'DISAPPROVED',
  'LIMITED',
  'IN_REVIEW',
  'LEARNING',
]);

/**
 * Bu durumdaki varlık YAYINDA SAYILMAZ ama kullanıcının müdahalesini de
 * beklemiyor olabilir. Otomasyon kapsam çözümü ve arayüz rozetleri aynı
 * listeyi okusun diye burada.
 */
export const ENTITY_STATUS_NEEDS_ATTENTION: readonly EntityStatus[] = ['DISAPPROVED', 'LIMITED'];

/// Prisma `RunStatus` — kural koşusunun sonucu.
export const RunStatus = {
  RUNNING: 'RUNNING',
  SUCCESS: 'SUCCESS',
  /// Aksiyonların bir kısmı uygulandı, bir kısmı hata aldı.
  PARTIAL: 'PARTIAL',
  FAILED: 'FAILED',
  /// Koşu hiç yapılmadı: bağlantı kopuk, entitlement yok, guardrail doldu.
  SKIPPED: 'SKIPPED',
  AWAITING_APPROVAL: 'AWAITING_APPROVAL',
} as const;
export type RunStatus = (typeof RunStatus)[keyof typeof RunStatus];
export const runStatusSchema = z.enum([
  'RUNNING',
  'SUCCESS',
  'PARTIAL',
  'FAILED',
  'SKIPPED',
  'AWAITING_APPROVAL',
]);

/// Prisma `ActionStatus` — tek bir aksiyonun akıbeti.
export const ActionStatus = {
  PENDING: 'PENDING',
  AWAITING_APPROVAL: 'AWAITING_APPROVAL',
  APPLIED: 'APPLIED',
  FAILED: 'FAILED',
  SKIPPED: 'SKIPPED',
  ROLLED_BACK: 'ROLLED_BACK',
} as const;
export type ActionStatus = (typeof ActionStatus)[keyof typeof ActionStatus];
export const actionStatusSchema = z.enum([
  'PENDING',
  'AWAITING_APPROVAL',
  'APPLIED',
  'FAILED',
  'SKIPPED',
  'ROLLED_BACK',
]);

/// Prisma `MessageRole` — ajan sohbetindeki mesajın kaynağı.
export const MessageRole = { USER: 'USER', ASSISTANT: 'ASSISTANT', TOOL: 'TOOL' } as const;
export type MessageRole = (typeof MessageRole)[keyof typeof MessageRole];
export const messageRoleSchema = z.enum(['USER', 'ASSISTANT', 'TOOL']);

/// Prisma `CreativeType`.
export const CreativeType = {
  AD_IMAGE: 'AD_IMAGE',
  PRODUCT_VIDEO: 'PRODUCT_VIDEO',
  UGC_VIDEO: 'UGC_VIDEO',
  /// Mevcut kreatifin metrik + görsel analiziyle yeniden tasarlanmışı.
  IMPROVED: 'IMPROVED',
  /// Aynı varlığın başka en-boy oranındaki hâli.
  RESIZED: 'RESIZED',
  /**
   * HAZIR KREATİF (Faz 8, 30 Eyl 2026) — kullanıcının kendi görseli/videosu,
   * sohbetten kütüphaneye alındı (`creative_import`). ÜRETİM TİPİ DEĞİL:
   * `creativeTypeSchema` (üretim girdisi) bu ikisini TAŞIMIYOR, kredi 0.
   */
  UPLOADED_IMAGE: 'UPLOADED_IMAGE',
  UPLOADED_VIDEO: 'UPLOADED_VIDEO',
  /**
   * REKLAM VİDEOSU (Kreatif stüdyo Faz 2, 1 Eki 2026) — bir PRODÜKSİYONUN
   * teslimi (`Creative.productionId` + `rendition`). Kütüphane, yayın, onay
   * ve indirme yolundan aynen geçer; geçmiş yalnız `master`ı listeler.
   *
   * ÜRETİM TİPİ DEĞİL (render kuyruğu bu tipi HİÇ görmez): `creativeTypeSchema`
   * taşımıyor, worker sağlayıcı kayıt defteri terminal retle kapatıyor, kredi
   * tablosunda 0 — bedeli prodüksiyon düzeyinde iki aşamalı tahsil ediliyor.
   * Yeniden üretim de yok: revizyon prodüksiyonun kendisinden.
   */
  AD_VIDEO: 'AD_VIDEO',
} as const;
export type CreativeType = (typeof CreativeType)[keyof typeof CreativeType];
/// Hazır (yüklenmiş) kreatif tipleri — üretilmedi, yeniden üretilemez.
export const UPLOADED_CREATIVE_TYPES: readonly CreativeType[] = ['UPLOADED_IMAGE', 'UPLOADED_VIDEO'];
export function isUploadedCreativeType(type: string): boolean {
  return (UPLOADED_CREATIVE_TYPES as readonly string[]).includes(type);
}
/**
 * Render kuyruğundan GEÇMEYEN tipler: hazır yükleme + prodüksiyon teslimi.
 * Fiyat yolu (`unitCreditCost`), "yeniden üret" kapısı ve sağlayıcı seçimi bu
 * tiplere girmez. `isUploadedCreativeType`tan ayrı: "kullanıcının kendi
 * dosyası" anlamı yalnız UPLOADED_* için doğru.
 */
export const NON_RENDERED_CREATIVE_TYPES: readonly CreativeType[] = [...UPLOADED_CREATIVE_TYPES, 'AD_VIDEO'];
export function isNonRenderedCreativeType(type: string): boolean {
  return (NON_RENDERED_CREATIVE_TYPES as readonly string[]).includes(type);
}
/**
 * Kütüphane SÜZGECİ — saklanan her tip. `creativeTypeSchema` üretim GİRDİSİ
 * (beş tip); süzgeç onunla kurulsaydı "Reklam videosu" ve hazır kreatifler
 * filtrelenemez, `?type=AD_VIDEO` 400 dönerdi.
 */
export const creativeLibraryTypeSchema = z.enum(CreativeType);
export const creativeTypeSchema = z.enum([
  'AD_IMAGE',
  'PRODUCT_VIDEO',
  'UGC_VIDEO',
  'IMPROVED',
  'RESIZED',
]);

/// Prisma `CreativeStatus`.
export const CreativeStatus = {
  DRAFT: 'DRAFT',
  GENERATING: 'GENERATING',
  READY: 'READY',
  FAILED: 'FAILED',
  PUBLISHED: 'PUBLISHED',
} as const;
export type CreativeStatus = (typeof CreativeStatus)[keyof typeof CreativeStatus];
export const creativeStatusSchema = z.enum(['DRAFT', 'GENERATING', 'READY', 'FAILED', 'PUBLISHED']);

/**
 * Prisma `CreativeApprovalStatus` — ekip içi onay (Şema borç turu).
 *
 * 🔴 `CreativeStatus` ile AYNI EKSEN DEĞİL. O varlığın ömrü (dosya üretiliyor
 * mu, hazır mı, yayında mı), bu insanın kararı. İkisi bağımsız: hazır ama
 * onaysız, hazır ve onaylı, hatta yayındayken sonradan reddedilmiş — hepsi
 * geçerli. Arayüz bu ikisini AYRI iki rozet olarak göstermeli; tek rozete
 * sıkıştırmak "onay bekleyen kreatifin dosyası hazır mı" sorusunu görünmez
 * kılar.
 */
export const CreativeApprovalStatus = {
  /// Bu çalışma alanında onay akışı kapalı (`creativeApprovalRequired=false`).
  NOT_REQUIRED: 'NOT_REQUIRED',
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
} as const;
export type CreativeApprovalStatus =
  (typeof CreativeApprovalStatus)[keyof typeof CreativeApprovalStatus];
export const creativeApprovalStatusSchema = z.enum([
  'NOT_REQUIRED',
  'PENDING',
  'APPROVED',
  'REJECTED',
]);

/// Prisma `ModerationSource` — kararı kim verdi.
export const ModerationSource = {
  /// Üretim sağlayıcısının kendi filtresi reddetti. Bugünkü TEK otomatik
  /// kaynak; ayrı bir tarama sağlayıcısı BAĞLANMADI (kullanıcı kararı).
  AUTO_PROVIDER: 'AUTO_PROVIDER',
  HUMAN: 'HUMAN',
} as const;
export type ModerationSource = (typeof ModerationSource)[keyof typeof ModerationSource];
export const moderationSourceSchema = z.enum(['AUTO_PROVIDER', 'HUMAN']);

/// Prisma `ModerationStatus`.
export const ModerationStatus = {
  CLEARED: 'CLEARED',
  /// Şüpheli — yayını ENGELLEMEZ, onaycıya uyarı olarak görünür. Kapı tek ve
  /// o `Creative.approvalStatus`.
  FLAGGED: 'FLAGGED',
  REJECTED: 'REJECTED',
} as const;
export type ModerationStatus = (typeof ModerationStatus)[keyof typeof ModerationStatus];
export const moderationStatusSchema = z.enum(['CLEARED', 'FLAGGED', 'REJECTED']);

/**
 * Moderasyon sınıfları — `ModerationVerdict.category`nin izinli değerleri.
 *
 * Prisma enum'u DEĞİL ve bu bilinçli: liste sağlayıcı taksonomisiyle birlikte
 * değişecek (fal bugün "content_policy" diyor, yarın kırılımı artırabilir) ve
 * her değişikliğin bir migration istemesi bu tabloyu donuk yapardı. Kısıt
 * uygulama katmanında: yazan tek yol `moderationCategorySchema`den geçiyor.
 *
 * ⚠️ Sağlayıcının kendi etiketini BURAYA eşlemek adapter'ın işi —
 * sınıflandırılmamış her şey `OTHER`a düşer, sessizce atılmaz.
 */
export const MODERATION_CATEGORIES = [
  /// Şiddet, nefret, kendine zarar — sağlayıcıların "safety" kovası.
  'SAFETY',
  /// Marka/telif ihlali: izinsiz logo, tanınmış karakter.
  'TRADEMARK',
  /// Yanıltıcı iddia — sağlık/finans vaadi, garanti dili.
  'MISLEADING',
  'ADULT',
  /// Sağlayıcı reddetti ama gerekçesini sınıflandıramadık. Boş bırakmak
  /// yerine bu değer yazılır: "sebep bilinmiyor" da bir cevap.
  'OTHER',
] as const;
export type ModerationCategory = (typeof MODERATION_CATEGORIES)[number];
export const moderationCategorySchema = z.enum(MODERATION_CATEGORIES);

/// Prisma `JobStatus` — render işinin durumu.
export const JobStatus = {
  QUEUED: 'QUEUED',
  RUNNING: 'RUNNING',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
  CANCELED: 'CANCELED',
} as const;
export type JobStatus = (typeof JobStatus)[keyof typeof JobStatus];
export const jobStatusSchema = z.enum(['QUEUED', 'RUNNING', 'SUCCEEDED', 'FAILED', 'CANCELED']);

/**
 * Prisma `NotificationChannel`.
 *
 * 🔴 `TELEGRAM` üç kez el değiştirdi — güncel karar 9 Eyl 2026'da GERİ EKLE.
 *
 * Faz 1'de eklendi, Faz 3'te kaldırıldı (*"tek işlevi bildirim atmaktı ve
 * WhatsApp aynı işi yapıyor"*), 9 Eyl 2026'da kullanıcı kararıyla geri geldi.
 * Kaldırma gerekçesi artık geçerli değil: WhatsApp bağlantısı RESMÎ DEĞİL
 * (Baileys/QR, numara banlanabilir, teslimat garantisi yok) ve tek kurumsal
 * numaraya bağlı — yani ürünün bildirim yolu tek bir banlanabilir numaranın
 * üstünde duruyordu. Telegram Bot API resmî ve ücretsiz; WhatsApp'ın YEDEĞİ
 * olarak duruyor, kopyası olarak değil.
 *
 * Bir kanalın VAR OLMASI ile SEÇİLEBİLİR olması ayrı iki soru:
 * `CHANNELS_WITH_TRANSPORT` ikincisini cevaplıyor. Kullanıcıya gönderilemeyecek
 * bir kanal seçtirmek, sessizce kaybolan bildirim demek.
 *
 * ⚠️ Kanalın YETENEKLERİ burada değil `notification-channels.ts`te
 * (`NOTIFICATION_CHANNEL_CAPABILITIES`). Bu dosya yalnız Prisma enum'unun
 * aynası; oraya bir değer eklemek o tabloyu DERLEME HATASI ile zorunlu kılar.
 */
export const NotificationChannel = {
  IN_APP: 'IN_APP',
  EMAIL: 'EMAIL',
  SLACK: 'SLACK',
  WHATSAPP: 'WHATSAPP',
  SMS: 'SMS',
  TELEGRAM: 'TELEGRAM',
  WEBHOOK: 'WEBHOOK',
} as const;
export type NotificationChannel = (typeof NotificationChannel)[keyof typeof NotificationChannel];
export const notificationChannelSchema = z.enum([
  'IN_APP',
  'EMAIL',
  'SLACK',
  'WHATSAPP',
  'SMS',
  'TELEGRAM',
  'WEBHOOK',
]);

/**
 * 🔴 BUGÜN GERÇEKTEN GÖNDERİM YAPILABİLEN KANALLAR.
 *
 * Faz 3'te Slack ve WhatsApp taşıyıcıları yazıldı — dördü de burada.
 *
 * ⚠️ Bir kanalın BU LİSTEDE olması taşıyıcının VAR olduğunu söyler, o
 * workspace'in onu BAĞLADIĞINI değil. Bağlantı ayrı bir soru
 * (`MessagingConnection` satırı var mı, `status` ne) ve onu
 * `MessagingService.connectedChannels()` cevaplıyor. Editör kullanıcıya
 * bağlanmamış bir kanalı seçtirirse mesaj hiçbir yere gitmez.
 */
export const CHANNELS_WITH_TRANSPORT: readonly NotificationChannel[] = [
  'IN_APP',
  'EMAIL',
  'SLACK',
  'WHATSAPP',
  // 9 Eyl 2026 — üçünün de sürücüsü yazıldı
  // (`apps/api/src/notifications/channels/`).
  'SMS',
  'TELEGRAM',
  'WEBHOOK',
];

/**
 * Prisma `MessagingChannel` — dış mesajlaşma kanalları (`MessagingConnection`).
 *
 * `NotificationChannel`in ALT KÜMESİ ama ayrı bir enum, çünkü `IN_APP` ve
 * `EMAIL` bir "bağlantı" gerektirmiyor: uygulama içi bildirimin bağlanacak bir
 * odası, e-postanın QR'ı yok. Tek enum olsaydı `MessagingConnection.channel`
 * kolonu `IN_APP` değerini kabul eder ve o satırın ne anlama geldiği
 * cevapsız kalırdı.
 */
export const MessagingChannel = {
  SLACK: 'SLACK',
  WHATSAPP: 'WHATSAPP',
  /**
   * 9 Eyl 2026 — kiracının kendi sistemine giden imzalı POST.
   *
   * Buraya giriyor çünkü `MessagingConnection`ın tanımına birebir uyuyor:
   * KİRACI başına tek adres + tek sır. `credentialEnc` imza sırrını,
   * `defaultDestination` hedef URL'i taşıyor — Slack'te webhook adresinin
   * sırrın KENDİSİ olmasıyla aynı desen.
   *
   * SMS ve Telegram buraya GİRMİYOR: onların adresi kişiye ait
   * (`NotificationContact`), kiracıya değil. Kiracı başına tek numara, üç
   * kişilik bir ekipte ikisini sessiz bırakırdı.
   */
  WEBHOOK: 'WEBHOOK',
} as const;
export type MessagingChannel = (typeof MessagingChannel)[keyof typeof MessagingChannel];
export const messagingChannelSchema = z.enum(['SLACK', 'WHATSAPP', 'WEBHOOK']);

/**
 * Prisma `AgentFocus` — ajanın TEK odak alanı (Faz 4).
 *
 * ⚠️ Bu ayna ile Prisma enum'u AYNI COMMIT'te güncellenir; ayrılırlarsa
 * `agentFocusSchema` veritabanının kabul ettiği bir değeri reddeder ve ajan
 * listesi o satırda patlar.
 *
 * 🔴 `KEYWORDS` Faz 5'te eklendi ve migration'ı (`20260812170000_faz5_ajan_
 * kelime_odagi`) UYGULANMADAN bu değeri taşıyan bir ajan KAYDEDİLEMEZ —
 * Postgres enum'u tanımıyor ve `agent.create` fırlatıyor. Okuma yolu
 * etkilenmiyor (var olan satırların hiçbiri bu değeri taşımıyor), yani
 * migration'dan önce de sonra da uygulamanın geri kalanı çalışır.
 */
export const AgentFocus = {
  BUDGET: 'BUDGET',
  CREATIVE: 'CREATIVE',
  BRAND_SAFETY: 'BRAND_SAFETY',
  /// Faz 5 — anahtar kelime bakımı: negatif kelime ekleme, kelime duraklatma,
  /// teklif ayarlama. YALNIZ Google hesaplarında iş görür (Meta'da kelime
  /// kavramı yok); ajan kartı bunu `canManageKeywords`e bakıp söylemeli.
  KEYWORDS: 'KEYWORDS',
} as const;
export type AgentFocus = (typeof AgentFocus)[keyof typeof AgentFocus];
export const agentFocusSchema = z.enum(['BUDGET', 'CREATIVE', 'BRAND_SAFETY', 'KEYWORDS']);

/**
 * 🔴 `CREDIT_COST` anahtarları `CreativeType` ile BİREBİR AYNI DEĞİL.
 *
 * Tabloda `IMPROVE` / `RESIZE` yazıyor, Prisma enum'unda `IMPROVED` /
 * `RESIZED`. `CREDIT_COST[creative.type]` bu iki tipte `undefined` döner ve
 * kredi düşümü SESSİZCE 0 olur — hata bile vermez, sadece para kaybettirir.
 *
 * Çözüm burada, tek yerde: kreatif tipinden maliyete giden yol yalnızca bu
 * tablo. `CREDIT_COST`'a doğrudan `CreativeType` ile indekslemek YASAK.
 */
export const CREDIT_COST_BY_CREATIVE_TYPE: Record<CreativeType, number> = {
  AD_IMAGE: CREDIT_COST.AD_IMAGE,
  PRODUCT_VIDEO: CREDIT_COST.PRODUCT_VIDEO,
  UGC_VIDEO: CREDIT_COST.UGC_VIDEO,
  IMPROVED: CREDIT_COST.IMPROVE,
  RESIZED: CREDIT_COST.RESIZE,
  /* Hazır kreatif — üretim yok, bedel yok. */
  UPLOADED_IMAGE: 0,
  UPLOADED_VIDEO: 0,
  /* Prodüksiyon teslimi — bedel PRODÜKSİYON düzeyinde (storyboard + üretim
     rezervleri, gerçek maliyetle kapanır). Kreatif satırına ikinci kez
     yazılsaydı aynı video iki kez faturalanmış görünürdü. */
  AD_VIDEO: 0,
};

// ═══════════════════════════════════════════════════════════════════════════
// GENİŞLEME ADIM 9 — ticari operasyon
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Fatura kesilen tarafın hukuki türü. Prisma `BillingEntityType` aynası.
 *
 * Neyin ZORUNLU olduğunu bu belirliyor: bireyselde 11 haneli TCKN,
 * kurumsalda 10 haneli VKN + vergi dairesi + ticari unvan. Numaranın
 * uzunluğuna bakıp türü tahmin etmek, 10 hane yazan bir bireyi sessizce
 * kuruma çevirirdi.
 */
export const BillingEntityType = {
  INDIVIDUAL: 'INDIVIDUAL',
  COMPANY: 'COMPANY',
} as const;
export type BillingEntityType = (typeof BillingEntityType)[keyof typeof BillingEntityType];
export const billingEntityTypeSchema = z.enum(['INDIVIDUAL', 'COMPANY']);

/// Ödemenin neye karşılık geldiği. Prisma `PaymentKind` aynası.
export const PaymentKind = {
  SUBSCRIPTION: 'SUBSCRIPTION',
  TOPUP: 'TOPUP',
} as const;
export type PaymentKind = (typeof PaymentKind)[keyof typeof PaymentKind];
export const paymentKindSchema = z.enum(['SUBSCRIPTION', 'TOPUP']);

/// Prisma `PaymentStatus` aynası. `FAILED` yok — gerekçe şemada: bu tabloya
/// yalnız TAHSİL EDİLMİŞ ödeme yazılıyor.
export const PaymentStatus = {
  SUCCEEDED: 'SUCCEEDED',
  PARTIALLY_REFUNDED: 'PARTIALLY_REFUNDED',
  REFUNDED: 'REFUNDED',
  DISPUTED: 'DISPUTED',
} as const;
export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];
export const paymentStatusSchema = z.enum([
  'SUCCEEDED',
  'PARTIALLY_REFUNDED',
  'REFUNDED',
  'DISPUTED',
]);

/// Prisma `RefundStatus` aynası. `FAILED` BURADA var: başarısız iade
/// görünmek zorunda.
export const RefundStatus = {
  PENDING: 'PENDING',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
} as const;
export type RefundStatus = (typeof RefundStatus)[keyof typeof RefundStatus];
export const refundStatusSchema = z.enum(['PENDING', 'SUCCEEDED', 'FAILED']);

/// Prisma `InvoiceKind` aynası. İade faturası ayrı bir belge.
export const InvoiceKind = { SALE: 'SALE', REFUND: 'REFUND' } as const;
export type InvoiceKind = (typeof InvoiceKind)[keyof typeof InvoiceKind];
export const invoiceKindSchema = z.enum(['SALE', 'REFUND']);

/// Prisma `InvoiceStatus` aynası.
export const InvoiceStatus = {
  PENDING: 'PENDING',
  ISSUED: 'ISSUED',
  FAILED: 'FAILED',
  CANCELED: 'CANCELED',
} as const;
export type InvoiceStatus = (typeof InvoiceStatus)[keyof typeof InvoiceStatus];
export const invoiceStatusSchema = z.enum(['PENDING', 'ISSUED', 'FAILED', 'CANCELED']);

/**
 * Demo talebinin hunideki yeri. Prisma `DemoRequestStatus` aynası.
 *
 * `GRANTED` "işi bitti" demek değil: 14 günlük tam erişim verildi demek.
 * Erişimin bitişi `grantedUntil`den okunuyor — durumu "süresi doldu"ya
 * çevirmek satış hunisinde "bu kişiye erişim verdik" bilgisini kaybettirirdi.
 */
export const DemoRequestStatus = {
  NEW: 'NEW',
  CONTACTED: 'CONTACTED',
  SCHEDULED: 'SCHEDULED',
  COMPLETED: 'COMPLETED',
  GRANTED: 'GRANTED',
  DECLINED: 'DECLINED',
} as const;
export type DemoRequestStatus = (typeof DemoRequestStatus)[keyof typeof DemoRequestStatus];
export const demoRequestStatusSchema = z.enum([
  'NEW',
  'CONTACTED',
  'SCHEDULED',
  'COMPLETED',
  'GRANTED',
  'DECLINED',
]);
